import { create } from "zustand"
import { DOC_STATUSES, type DocFrame, type DocumentRow, type DocStatus } from "./contract"
import { listDocuments, uploadDocument } from "./api"
import { followDocuments } from "./stream"

export interface DocView {
  docId: string
  filename: string
  status: DocStatus
  pagesTotal: number
  pagesDone: number
  pagesFailed: number
  summary?: Record<string, unknown> | null
  error?: string
  /** Server time the upload was created; only known for documents that came from a list or upload response. */
  createdAt?: string
}

export interface DocsThreadView {
  /** Highest document-stream seq applied. Frames at or below it are duplicates. */
  lastSeq: number
  docs: Record<string, DocView>
}

export const emptyDocsThread = (): DocsThreadView => ({ lastSeq: 0, docs: {} })

const blank = (docId: string): DocView => ({
  docId, filename: "", status: "queued", pagesTotal: 0, pagesDone: 0, pagesFailed: 0,
})

const num = (v: unknown, fallback: number) => (typeof v === "number" ? v : fallback)
const str = (v: unknown) => (typeof v === "string" ? v : undefined)

/** Pure reducer for one document-stream frame. Frames at or below lastSeq are no-ops. */
export function reduceDocs(view: DocsThreadView, frame: DocFrame): DocsThreadView {
  if (frame.seq <= view.lastSeq) return view
  const p = frame.payload
  const docId = frame.doc_id ?? str(p.doc_id)
  if (!docId) return { ...view, lastSeq: frame.seq }
  const cur = view.docs[docId] ?? blank(docId)
  let next: DocView = cur
  switch (frame.type) {
    case "doc.progress": {
      const known = (DOC_STATUSES as readonly string[]).includes(str(p.status) ?? "")
      next = {
        ...cur,
        filename: str(p.filename) ?? cur.filename,
        status: known ? (p.status as DocStatus) : cur.status,
        pagesTotal: num(p.pages_total, cur.pagesTotal),
        pagesDone: num(p.pages_done, cur.pagesDone),
        pagesFailed: num(p.pages_failed, cur.pagesFailed),
      }
      break
    }
    // A replay coalesces each document to its latest frame, so the terminal frames carry the filename too.
    case "doc.ready":
      next = {
        ...cur,
        filename: str(p.filename) ?? cur.filename,
        status: "ready",
        pagesTotal: num(p.pages_total, cur.pagesTotal),
        pagesDone: num(p.pages_done, cur.pagesDone),
        pagesFailed: num(p.pages_failed, cur.pagesFailed),
        summary: (p.summary as Record<string, unknown>) ?? null,
      }
      break
    case "doc.error":
      next = { ...cur, filename: str(p.filename) ?? cur.filename, status: "failed", error: str(p.message) ?? str(p.reason) ?? "failed" }
      break
  }
  return { lastSeq: frame.seq, docs: { ...view.docs, [docId]: next } }
}

interface DocsStore {
  threads: Record<string, DocsThreadView>
  apply: (threadId: string, frame: DocFrame) => void
  /** Overwrite statuses from a list snapshot. */
  setRows: (threadId: string, rows: DocumentRow[]) => void
  /** Add an upload response. A known document is never overwritten: progress may already have arrived. */
  addUpload: (threadId: string, row: DocumentRow) => void
  /** Upload a file to a thread and track it. */
  upload: (threadId: string, file: File) => Promise<DocumentRow>
  /** Load the document list, then follow the document stream from seq 0 until `signal` aborts. A failed list still follows. */
  hydrate: (threadId: string, signal?: AbortSignal) => Promise<void>
  clear: () => void
}

const rowToView = (r: DocumentRow): DocView => ({
  docId: r.doc_id, filename: r.filename, status: r.status, pagesTotal: r.pages_total,
  pagesDone: r.pages_done, pagesFailed: r.pages_failed, summary: r.summary, error: r.error ?? undefined, createdAt: r.created_at,
})

export const useDocsStore = create<DocsStore>((set, get) => ({
  threads: {},
  apply: (threadId, frame) =>
    set((s) => {
      const cur = s.threads[threadId] ?? emptyDocsThread()
      const next = reduceDocs(cur, frame)
      return next === cur ? s : { threads: { ...s.threads, [threadId]: next } }
    }),
  setRows: (threadId, rows) =>
    set((s) => {
      const cur = s.threads[threadId] ?? emptyDocsThread()
      const docs = { ...cur.docs }
      for (const r of rows) {
        docs[r.doc_id] = { ...(docs[r.doc_id] ?? blank(r.doc_id)), ...rowToView(r) }
      }
      return { threads: { ...s.threads, [threadId]: { ...cur, docs } } }
    }),
  addUpload: (threadId, row) =>
    set((s) => {
      const cur = s.threads[threadId] ?? emptyDocsThread()
      if (cur.docs[row.doc_id]) {
        const docs = { ...cur.docs, [row.doc_id]: { ...cur.docs[row.doc_id], filename: row.filename } }
        return { threads: { ...s.threads, [threadId]: { ...cur, docs } } }
      }
      const docs = { ...cur.docs, [row.doc_id]: rowToView(row) }
      return { threads: { ...s.threads, [threadId]: { ...cur, docs } } }
    }),
  upload: async (threadId, file) => {
    const row = await uploadDocument(threadId, file)
    get().addUpload(threadId, row)
    return row
  },
  hydrate: async (threadId, signal) => {
    // The follow replays every document from seq 0, so a failed list loses nothing it needs: follow anyway.
    try {
      get().setRows(threadId, await listDocuments(threadId, signal))
    } catch {
      if (signal?.aborted) return
    }
    await followDocuments(threadId, 0, (frame) => get().apply(threadId, frame), signal)
  },
  clear: () => set({ threads: {} }),
}))
